/* -------------------------------------------------------------------------- */
/*                        ANCHOR Utilidades de color                           */
/* -------------------------------------------------------------------------- */

export const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export function isHex(value: string | null | undefined): boolean {
  return !!value && HEX_RE.test(value);
}

export function normalizeHex(value: string): string {
  let v = (value || '').trim();
  if (!v.startsWith('#')) v = '#' + v;
  if (/^#[0-9A-Fa-f]{3}$/.test(v)) v = '#' + v.slice(1).split('').map(c => c + c).join('');
  return v.toUpperCase();
}

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

export function luminance(hex: string): number {
  if (!isHex(hex)) return 0;
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

/** Ratio de contraste WCAG entre dos colores hex. */
export function contrastRatio(a: string, b: string): number {
  const l1 = luminance(a);
  const l2 = luminance(b);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 10) / 10;
}

/** Color de tinta legible (#111316 o blanco) sobre un fondo. */
export function inkFor(bg: string): string {
  return luminance(bg) > 0.45 ? '#111316' : '#FFFFFF';
}

/** Color pastel determinista a partir de un texto (logos sin imagen). */
const BRAND_TINTS = ['#BCDDE8', '#F6D9C4', '#E9D5EC', '#DDEBD3', '#FBEBC5', '#D3E4EA', '#F8D9D6', '#D7EBE8'];
const BRAND_INKS = ['#0A3B4A', '#6B2E12', '#4A1F5A', '#1F4A2A', '#5A4300', '#173E52', '#6B2130', '#063F3E'];

export function tintFor(seed: string): { bg: string; ink: string } {
  let h = 0;
  for (const ch of seed || '') h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const i = h % BRAND_TINTS.length;
  return { bg: BRAND_TINTS[i], ink: BRAND_INKS[i] };
}

/**
 * Tinte de marca del negocio: `brandColor` de la API (misma regla en app y console-app) o, si no hay
 * tarjeta publicada, el pastel determinista por nombre. `bg` = pastel para portadas; `ink` = texto legible.
 */
export function brandTint(brandColor: string | null | undefined, name: string, amount = 0.8): { bg: string; ink: string } {
  if (isHex(brandColor)) {
    const hex = normalizeHex(brandColor!);
    return { bg: pastel(hex, amount), ink: luminance(hex) < 0.2 ? hex : '#111316' };
  }
  return tintFor(name);
}

/** Mezcla un color con blanco (portadas pastel de las tarjetas de negocio). */
export function pastel(hex: string | null | undefined, amount = 0.78): string {
  if (!isHex(hex)) return '#E4E7EC';
  const n = parseInt(hex!.slice(1), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  const r = mix((n >> 16) & 255), g = mix((n >> 8) & 255), b = mix(n & 255);
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
}
